import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useViewAs } from '@/contexts/ViewAsContext';

interface CourseAccessResult {
  hasAccess: boolean | null; // null = still checking
  loading: boolean;
  courseId: string | null;
}

/**
 * Check if current user has access to a course.
 * Accepts a courseId directly, or resolves it from a lessonId or taskId.
 */
export const useCourseAccess = ({
  courseId,
  lessonId,
  taskId,
}: {
  courseId?: string | null;
  lessonId?: string | null;
  taskId?: string | null;
}): CourseAccessResult => {
  const { user, isStaff, loading: authLoading } = useAuth();
  const { active, overrides } = useViewAs();
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [resolvedCourseId, setResolvedCourseId] = useState<string | null>(courseId ?? null);

  useEffect(() => {
    // Wait for auth to finish loading before checking access
    if (authLoading) return;

    let cancelled = false;

    const check = async () => {
      setLoading(true);
      setHasAccess(null);

      // Admin always has access
      if (isStaff && !(active && overrides.simulateRegularUser)) {
        if (!cancelled) { setHasAccess(true); setLoading(false); }
        return;
      }

      // Check if courses are disabled globally
      const { data: appSettings } = await supabase
        .from('app_settings' as any)
        .select('courses_enabled')
        .eq('id', 1)
        .single();
      if (cancelled) return;
      if (appSettings && !(appSettings as any).courses_enabled) {
        setHasAccess(false);
        setLoading(false);
        return;
      }

      // Resolve courseId from lessonId or taskId if needed
      let cId = courseId ?? null;
      let lessonRequiresPremium = false;

      if (lessonId) {
        const { data } = await supabase
          .from('lessons')
          .select('course_id, required_tier')
          .eq('id', lessonId)
          .single();
        if (cancelled) return;
        if (!cId) cId = data?.course_id ?? null;
        lessonRequiresPremium = (data?.required_tier ?? 'standard') === 'premium';
      }

      if (!cId && taskId) {
        const { data } = await supabase
          .from('tasks')
          .select('course_id')
          .eq('id', taskId)
          .single();
        if (cancelled) return;
        cId = data?.course_id ?? null;
      }

      setResolvedCourseId(cId);

      // Premium-only lessons are never free, even if the course itself is
      if (!cId && !lessonRequiresPremium) {
        setHasAccess(true);
        setLoading(false);
        return;
      }

      // Check if the course is free (no subscription, no price)
      const { data: course } = cId
        ? await supabase
            .from('courses')
            .select('included_in_subscription, stripe_price_id')
            .eq('id', cId)
            .single()
        : { data: null };
      if (cancelled) return;

      if (!course && !lessonRequiresPremium) {
        setHasAccess(true);
        setLoading(false);
        return;
      }

      if (!course?.included_in_subscription && !course?.stripe_price_id && !lessonRequiresPremium) {
        setHasAccess(true);
        setLoading(false);
        return;
      }

      // Not logged in = no access to paid course
      if (!user) {
        setHasAccess(false);
        setLoading(false);
        return;
      }

      // ViewAs simulation: decide access based on simulated tier instead of server check
      if (active && overrides.simulateRegularUser && overrides.tier) {
        if (overrides.tier === 'free') {
          // Free tier: no access to any paid course
          setHasAccess(false);
        } else if (lessonRequiresPremium) {
          setHasAccess(overrides.tier === 'premium');
        } else {
          // Standard or Premium: simulate having purchased with that tier
          setHasAccess(true);
        }
        setLoading(false);
        return;
      }

      // Server-side check
      try {
        const { data, error } = await supabase.functions.invoke('check-course-access', {
          body: { courseId: cId },
        });
        if (cancelled) return;
        if (error) throw error;
        let granted = data?.hasAccess ?? false;
        // Premium-only lessons need a premium tier on the course/block/lesson
        if (granted && lessonRequiresPremium && lessonId) {
          const { data: lessonAllowed } = await supabase.rpc('can_access_lesson', { _lesson_id: lessonId });
          if (cancelled) return;
          granted = !!lessonAllowed;
        }
        setHasAccess(granted);
      } catch {
        if (!cancelled) setHasAccess(false);
      }

      if (!cancelled) setLoading(false);
    };

    check();
    return () => { cancelled = true; };
  }, [courseId, lessonId, taskId, user, isStaff, authLoading, active, overrides]);

  return { hasAccess, loading, courseId: resolvedCourseId };
};
